import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Stack, Paper, Divider } from '@mui/material';
import { SendOutlined } from '@mui/icons-material';

interface Comment {
  _id?: string;
  user?: { name?: string; email?: string };
  text?: string;
  createdAt?: string;
}

interface CommentsPanelProps {
  comments: Comment[];
  isReadOnly?: boolean;
  onAddComment?: (text: string) => void;
}

export const CommentsPanel: React.FC<CommentsPanelProps> = ({ comments, isReadOnly, onAddComment }) => {
  const [newComment, setNewComment] = useState('');

  const handleSubmit = () => {
    if (!newComment.trim() || !onAddComment) return;
    onAddComment(newComment.trim());
    setNewComment('');
  };

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Comments ({comments.length})
      </Typography>

      {/* Comment list */}
      <Box sx={{ flex: 1, overflow: 'auto', mb: 1 }}>
        {comments.length > 0 ? (
          <Stack spacing={1}>
            {comments.map((c, i) => (
              <Paper key={c._id || i} variant="outlined" sx={{ p: 1 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {c.user?.name || c.user?.email || 'Unknown'}
                  </Typography>
                  {c.createdAt && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.createdAt).toLocaleString()}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="body2">{c.text}</Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No comments yet.</Typography>
        )}
      </Box>

      {/* Add comment */}
      <Divider sx={{ mb: 1 }} />
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          placeholder={isReadOnly ? "Comments are read-only" : "Add a comment…"}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
          multiline
          maxRows={3}
          disabled={isReadOnly}
        />
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={!newComment.trim() || isReadOnly}
          sx={{ minWidth: 40 }}
        >
          <SendOutlined fontSize="small" />
        </Button>
      </Stack>
    </Box>
  );
};
